import { Configuration } from '@Types/configuration.types';
import { ReduxMetaData } from '@Types/reduxMetaData.types';
import { SensorType } from '@innovyze/shared-utils';
import { PaginationInfo } from './pagination.types';
import { STATUS_ENUM } from '@innovyze/stylovyze';

export interface ConfigurationRow extends Configuration {
	selected?: boolean;
}

export type SensorTypeFilter = SensorType | 'All';

export type PhysicalOrVirtual = 'all' | 'physical' | 'virtual';

export interface ConfigurationsStoreState extends ReduxMetaData {
	list: ConfigurationRow[];
	selectedConfig?: Configuration;
	pagination: PaginationInfo;
	searchValue: string;
	physicalOrVirtual: PhysicalOrVirtual;
	createSensorStatus: STATUS_ENUM;
	deletionInProgress: boolean;
	sensorTypes: SensorTypeFilter[];
	dataSourceTypes?: string[];
	loadingConfigurationDetails: boolean;
	checkOrUncheckedRows: {
		sensorId: string;
		sensorType: SensorType;
		dataSourceType: string;
	}[];
	headerCheckboxChecked: boolean;
	errorCategory: string;
	errorMsg: string;
	configurationById: {
		configuration?: Configuration;
		status: STATUS_ENUM;
	};
	rowSelectionModel: string[];
	paginationModel: {
		page: number;
		pageSize: number;
	};
}

export type PartialConfigurationsStoreState = Partial<ConfigurationsStoreState>;

export interface GetConfigurationsResponse {
	configurations: Configuration[];
}

export interface UpdateConfigurationResponse {
	configuration: Configuration;
}

export interface PaginatedConfigurations {
	configurations: Configuration[];
	pagination: PaginationInfo;
}
export interface ConfigurationParams {
	configType?: string;
	limit?: number;
	offset?: number;
	q?: string;
	sensorIds?: string;
	virtualOrPhysical?: 'physical' | 'virtual' | 'all';
	allInQParam?: boolean;
	isIncludeLastValue?: boolean;
}

export interface MultipleDeleteParams {
	virtualOrPhysical: PhysicalOrVirtual;
	sensorTypes: SensorTypeFilter[];
	checkOrUncheckedRows: { sensorId: string; sensorType: SensorType }[];
	headerCheckboxChecked: boolean;
	onlyData: boolean;
	searchValue: string;
	dataSourceTypes?: string[];
}

export type ConfigurationParamsRfi = {
	fileName: string;
	requestParams: Required<
		Pick<
			ConfigurationParams,
			'offset' | 'limit' | 'virtualOrPhysical' | 'q'
		>
	>;
};

export interface ConfigurationParamsWithSensorId extends ConfigurationParams {
	sensorId: string;
}

export type SensorConfigFormShape = {
	dataSourceName?: string;
	assetId: string;
	collectionInterval: string;
	dataSourceId: string;
	dataSourceType: string;
	interpolationEnabled: string;
	interpolationMethod: string;
	location: string;
	sensorAlias: string;
	sensorChannelField: string;
	sensorDateField: string;
	sensorId: string;
	sensorIdField: string;
	sensorTable: string;
	sensorTimeField: string;
	sensorType: string;
	sensorUnitConversion: string;
	tagValue: string;
	tankCurveId: string;
	unit: string;
	tankType: 'cylindrical' | 'non-cylindrical';
	tankOffset: string;
	tankDiameter: string;
	isPhysical?: string;
};
export interface UpdateConfigElParams {
	idx: number;
	value: Configuration;
}
export type RemapConfigParams = UpdateConfigElParams['value'];

export interface CreateConfigurationParams {
	sensorId?: string;
	connectionString?: string;
	sensorType?: string;
	unit?: string;
	collectionInterval?: string;
	assetId?: string;
	sensorAlias?: string;
	location?: string;
	sensorTable?: string;
	sensorDateField?: string;
	sensorTimeField?: string;
	sensorChannelField?: string;
	sensorUnitConversion?: string;
	configType?: string;
	enableTelemetry?: boolean;
	sensorIdField?: string;
	interpolationEnabled?: string;
	interpolationMethod?: string;
	tankCurveId?: string;
	tankType?: 'cylindrical' | 'non-cylindrical';
	tankOffset?: number | null;
	tankDiameter?: number | null;
	dataSourceName?: string;
	dataSourceId?: string;
	dataSourceType: string;
	tagValue?: string;
	isPhysical?: boolean;
}

export interface DeleteConfigurationParams {
	sensorId: string;
}

export enum ConfigurationsErrors {
	DuplicationError = 'DuplicationError',
}

export const DEFAULT_SENSOR_CONFIGURATIONS_GRID_COLUMNS = [
	{ name: 'sensorAlias', order: 0 },
	{ name: 'dataSourceType', order: 1 },
	{ name: 'lastValue', order: 2 },
	{ name: 'lastValueTimestamp', order: 3 },
	{ name: 'unit', order: 4 },
];

export enum ConfigurationsActions {
	EDIT = 'edit',
	CHART = 'chart',
	LOCATE_ON_MAP = 'locateOnMap',
	TEST = 'sensorTest',
}
export enum ConfigurationDataSourceType {
	Form = 'FORM',
	ODBC = 'ODBC',
	OPCUA = 'OPC-UA',
	OLEDB = 'OLEDB',
	EMAGIN = 'emagin',
	MASS_BALANCE = 'mass balance',
	CUSTOM_ANALYTICS = 'custom analytics',
	ILI = 'ili',
	BULK_UPLOAD = 'Bulk Upload',
}

// Will be updated after adding API capability to retreive virtual sensors reading counts
export const SensorsReadingCountFileName = 'physical_sensors_count.csv';
